import { computed, ref, watch } from 'vue';
import type { AuthUser } from '../types/auth';
import { supabaseClient } from '@/services/supabase';
import { isNonNullable, isNullable } from '@/utils/nullability-helpers';
import { fetchUser } from '@/services/auth-service';
import { createGlobalState, useLocalStorage } from '@vueuse/core';
import type { Organization } from '@/types/organizations';
import type { OrganizationMetadata } from '@shared-types/organizations';
import { defaultOrganizationId } from '@/utils/env-helpers';
import * as Sentry from '@sentry/vue';

export const useAuthStore = createGlobalState(() => {
    const user = ref<AuthUser | undefined>(undefined);
    const isFetchingUser = ref(false);
    let isFetchingUserPromise: Promise<AuthUser | undefined> | undefined;

    const isSignedIn = computed(() => isNonNullable(user.value));

    async function fetchUserIfNecessary(): Promise<AuthUser | undefined> {
        if (isNullable(user.value)) {
            if (isFetchingUser.value) {
                return isFetchingUserPromise;
            }
            try {
                isFetchingUser.value = true;
                isFetchingUserPromise = fetchUser();
                user.value = await isFetchingUserPromise;
            } finally {
                isFetchingUserPromise = undefined;
                isFetchingUser.value = false;
            }
        }
        return user.value;
    }

    async function refreshUser(): Promise<AuthUser | undefined> {
        user.value = await fetchUser();
        return user.value;
    }

    supabaseClient.auth.onAuthStateChange((_, session) => {
        user.value = session?.user as AuthUser | undefined;
    });

    const possibleOrganizationIds = computed(() => {
        if (isNullable(user.value?.app_metadata.organizations)) {
            return [];
        }
        return Object.keys(user.value.app_metadata.organizations);
    });

    const userSelectedOrganizationId = useLocalStorage<string | undefined>('userSelectedOrganizationId', undefined);

    const selectedOrganizationId = computed<string | undefined>(() => {
        if (
            isNonNullable(userSelectedOrganizationId.value) &&
            possibleOrganizationIds.value.includes(userSelectedOrganizationId.value)
        ) {
            return userSelectedOrganizationId.value;
        }
        return possibleOrganizationIds.value[0];
    });

    const selectedOrganizationRole = computed<string | undefined>(() => {
        if (isNullable(selectedOrganizationId.value)) {
            return [];
        }
        return user.value?.app_metadata.organizations[selectedOrganizationId.value][0];
    });

    const loadedOrganizations = ref<Organization[]>([]);

    // refresh the page when the group is changed to load relevant group related data
    watch(userSelectedOrganizationId, (newOrganizationId, oldOrganizationId) => {
        if (newOrganizationId !== oldOrganizationId) {
            window.location.reload();
        }
    });

    watch(
        possibleOrganizationIds,
        async () => {
            const { data } = await supabaseClient.from('organizations').select('*');
            if (isNonNullable(data)) {
                loadedOrganizations.value = data.map((organization) => ({
                    id: organization.id,
                    metadata: organization.metadata as OrganizationMetadata,
                    status: organization.status as 'active' | 'suspended',
                    suspendedAt: organization.suspended_at,
                    suspendedBy: organization.suspended_by,
                    suspensionReason: organization.suspension_reason,
                    autoDeleteAt: organization.auto_delete_at,
                }));
            }
        },
        { immediate: true },
    );

    const organizations = computed(() =>
        possibleOrganizationIds.value
            .map((id) => loadedOrganizations.value.find((g) => g.id === id))
            .filter(isNonNullable),
    );

    const currentOrganization = computed(() =>
        loadedOrganizations.value.find((g) => g.id === selectedOrganizationId.value),
    );

    // Default organization is the one that has super admin privileges
    // We should probably create a different app (codebase) for super admins to manage application data
    // or think of something else
    // Handling the access just via javascript is not so secure
    const isDefaultOrganization = computed(() => selectedOrganizationId.value === defaultOrganizationId);

    function onOrganizationSwitch(callback: () => void) {
        watch(selectedOrganizationId, (newOrganizationId, oldOrganizationId) => {
            if (isNonNullable(oldOrganizationId) && newOrganizationId !== oldOrganizationId) {
                callback();
            }
        });
    }

    const isLoggedIn = computed(() => isNonNullable(user.value));
    const isAdmin = computed(() => selectedOrganizationRole.value === 'admin');
    // const isDepartmentAdmin = computed(() => selectedOrganizationRole.value === 'department_admin');
    const isInstructor = computed(
        () => selectedOrganizationRole.value === 'instructor' || selectedOrganizationRole.value === 'admin',
    );

    const isSuperAdmin = computed(() => {
        if (isNullable(user.value)) {
            return false;
        }
        return user.value?.app_metadata.organizations[defaultOrganizationId][0] === 'admin';
    });

    watch(
        user,
        (newUser) => {
            if (isNonNullable(newUser)) {
                Sentry.setUser({
                    id: newUser.id,
                });
            } else {
                Sentry.setUser(null);
            }
        },
        { immediate: true },
    );

    return {
        user,
        isSignedIn,
        organizationId: selectedOrganizationId,
        organizations,
        currentOrganization,
        isDefaultOrganization,
        userSelectedOrganizationId,
        possibleOrganizationIds,
        onOrganizationSwitch,
        role: selectedOrganizationRole,
        isInstructor,
        isAdmin,
        // isDepartmentAdmin,
        isLoggedIn,
        fetchUserIfNecessary,
        refreshUser,
        isFetchingUser,
        isSuperAdmin,
    };
});
